// src/components/professor/SessionList.js
import React, { useState } from 'react';
import { db } from '../../../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import {
  getDatabase,
  ref as refRealtime,
  get as getRealtime,
  update as updateRealtime
} from 'firebase/database';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import Preloader from '../../Preloader';

const SessionList = ({ sessions, classId, fetchClassDetails, theme }) => {
  const [sessionSearchTerm, setSessionSearchTerm] = useState('');
  const [expandedSessions, setExpandedSessions] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});

  const handleSessionSearch = (e) => {
    setSessionSearchTerm(e.target.value);
  };

  const toggleSessionExpand = (sessionId) => {
    setExpandedSessions({
      ...expandedSessions,
      [sessionId]: !expandedSessions[sessionId],
    });
  };

  // Filter sessions by date string
  const filteredSessions = sessions.filter((session) => {
    const sessionDate = new Date(session.start_date_time);
    return sessionDate.toLocaleDateString().includes(sessionSearchTerm);
  });

  const formatAnswer = (answer) =>
    answer
      .toString()
      .toUpperCase()
      .split(',')
      .map((a) => a.trim())
      .filter((a) => a !== '');

  const updateCorrectAnswer = async (sessionId, questionNumber, newAnswer) => {
    setLoading(true);
    try {
      const formattedAnswer = formatAnswer(newAnswer);
      const dbRealtime = getDatabase();
      const sessionQuestionsRef = refRealtime(
        dbRealtime,
        `sessions/${classId}/${sessionId}/questions/${questionNumber}`
      );

      // Update the correct_answer in Realtime Database
      await updateRealtime(sessionQuestionsRef, { correct_answer: formattedAnswer });

      // Regrade students based on the new correct answer
      await regradeStudents(sessionId, questionNumber, formattedAnswer);

      alert('Correct answer updated and students re-graded.');
      await fetchClassDetails();
    } catch (error) {
      console.error('Error updating correct answer:', error);
      alert('Failed to update correct answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const regradeStudents = async (sessionId, questionNumber, newAnswer) => {
    try {
      const dbRealtime = getDatabase();
      const answersRef = refRealtime(
        dbRealtime,
        `answers/${classId}/${sessionId}/${questionNumber}`
      );
      const snapshot = await getRealtime(answersRef);
      if (snapshot.exists()) {
        const answersData = snapshot.val();
        const gradesDocRef = doc(db, 'grades', classId);

        const session = sessions.find(s => s.id === sessionId);
        if (!session) {
          console.error(`Session with ID ${sessionId} not found.`);
          alert(`Session with ID ${sessionId} not found.`);
          return;
        }

        const question = session.questions[questionNumber];
        if (!question) {
          console.error(`Question number ${questionNumber} not found in session ${sessionId}.`);
          alert(`Question number ${questionNumber} not found in session ${sessionId}.`);
          return;
        }

        const questionType = question.question_type;
        const formattedCorrectAnswer = newAnswer;
        const gradeUpdates = {};

        for (const studentUid in answersData) {
          const studentAnswerRaw = answersData[studentUid].answer;
          const studentAnswer = formatAnswer(studentAnswerRaw);
          let pointsEarned = 0;

          if (questionType === 'multiple_choice') {
            if (studentAnswer.length > 0) {
              const isCorrect = formattedCorrectAnswer.includes(studentAnswer[0]);
              pointsEarned = isCorrect ? 1 : 0;
            }
          } else if (questionType === 'multiple_multiple_choice') {
            const totalCorrectAnswers = formattedCorrectAnswer.length;
            if (totalCorrectAnswers > 0) {
              const pointPerCorrectAnswer = 1 / totalCorrectAnswers;
              const correctSelections = studentAnswer.filter(answer =>
                formattedCorrectAnswer.includes(answer)
              ).length;
              const incorrectSelections = studentAnswer.filter(answer =>
                !formattedCorrectAnswer.includes(answer)
              ).length;
              pointsEarned =
                (correctSelections * pointPerCorrectAnswer) -
                (incorrectSelections * pointPerCorrectAnswer);
              pointsEarned = Math.max(0, Math.round(pointsEarned * 100) / 100);
            }
          } else if (questionType === 'yes_no') {
            if (studentAnswer.length > 0) {
              const isCorrect = formattedCorrectAnswer.includes(studentAnswer[0]);
              pointsEarned = isCorrect ? 1 : 0;
            }
          } else if (questionType === 'short_input') {
            if (studentAnswer[0]) {
              // case-insensitive match against any of the professor’s answers
              const isCorrect = formattedCorrectAnswer.some(correct =>
                correct.trim().toLowerCase() === studentAnswer[0].trim().toLowerCase()
              );
              pointsEarned = isCorrect ? 1 : 0;
            }
          }
          gradeUpdates[`students.${studentUid}.${sessionId}.points.${questionNumber}`] = pointsEarned;
        }

        if (Object.keys(gradeUpdates).length > 0) {
          await updateDoc(gradesDocRef, gradeUpdates);
          console.log('Grades updated successfully.');
        } else {
          console.log('No grades to update.');
        }
      } else {
        console.log('No answers found for regrading.');
        alert('No answers found for regrading.');
      }
    } catch (error) {
      console.error('Error regrading students:', error);
      alert('Failed to regrade students. Please check the console for more details.');
    }
  };

  const handleImageUpload = async (sessionId, questionNumber, file) => {
    setLoading(true);
    try {
      const storage = getStorage();
      const storageReference = storageRef(
        storage,
        `questions/${classId}/${sessionId}/${questionNumber}/question_image.jpg`
      );
      await uploadBytes(storageReference, file);
      const downloadURL = await getDownloadURL(storageReference);

      const dbRealtime = getDatabase();
      const questionRef = refRealtime(
        dbRealtime,
        `sessions/${classId}/${sessionId}/questions/${questionNumber}`
      );
      await updateRealtime(questionRef, { question_photo: downloadURL });

      alert('Image uploaded successfully.');
      await fetchClassDetails();

      setSelectedFiles((prevFiles) => {
        const key = `${sessionId}-${questionNumber}`;
        const newFiles = { ...prevFiles };
        delete newFiles[key];
        return newFiles;
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageRemove = async (sessionId, questionNumber) => {
    setLoading(true);
    try {
      const storage = getStorage();
      const storageReference = storageRef(
        storage,
        `questions/${classId}/${sessionId}/${questionNumber}/question_image.jpg`
      );
      await deleteObject(storageReference);

      const dbRealtime = getDatabase();
      const questionRef = refRealtime(
        dbRealtime,
        `sessions/${classId}/${sessionId}/questions/${questionNumber}`
      );
      await updateRealtime(questionRef, { question_photo: "InClass" });

      alert('Image removed successfully.');
      await fetchClassDetails();
    } catch (error) {
      console.error('Error removing image:', error);
      alert('Error removing image.');
    } finally {
      setLoading(false);
    }
  };

  const questionTypeDisplay = {
    'multiple_choice': 'Multiple Choice',
    'multiple_multiple_choice': 'Multiple Answers',
    'yes_no': 'Yes/No',
    'short_input': 'Input',
  };

  if (loading) {
    return <Preloader />;
  }

  return (
    <div className="w-full min-w-0 px-3 sm:px-4 py-4">
      <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-color)' }}>
        Sessions
      </h3>
      <input
        type="text"
        placeholder="Search Sessions..."
        value={sessionSearchTerm}
        onChange={handleSessionSearch}
        className="w-full p-2 mb-4 border border-[var(--muted-color)] rounded bg-[var(--card-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                           focus:border-transparent transition-all"
        style={{
          backgroundColor: 'var(--card-bg)',
          color: 'var(--text-color)',
          borderColor: 'var(--muted-color)'
        }}
      />

      <div className="space-y-4">
        {filteredSessions.map((session) => {
          const maxQuestionNumber = session.questions
            ? Math.max(...Object.keys(session.questions).map(Number))
            : 0;
          return (
            <div
              key={session.id}
              className="border rounded shadow-sm"
              style={{ borderColor: 'var(--muted-color)' }}
            >
              <div
                className="flex justify-between items-center p-3 rounded cursor-pointer"
                style={{ backgroundColor: 'var(--card-hover-bg)' }}
                onClick={() => toggleSessionExpand(session.id)}
              >
                <h4 className="text-lg font-medium" style={{ color: 'var(--text-color)' }}>
                  Session on {new Date(session.start_date_time).toLocaleString()}
                </h4>
                <span className="text-xl" style={{ color: 'var(--text-color)' }}>
                  {expandedSessions[session.id] ? '▴' : '▾'}
                </span>
              </div>

              {expandedSessions[session.id] && (
                <div className="p-4" style={{ backgroundColor: 'var(--card-bg)' }}>
                  <p className="mb-4" style={{ color: 'var(--text-color)' }}>
                    <strong>Total Questions:</strong> {maxQuestionNumber}
                  </p>
                  {session.questions &&
                    Object.entries(session.questions).map(([questionNumber, question]) => (
                      <div
                        key={questionNumber}
                        className="mb-4 p-4 border rounded"
                        style={{ borderColor: 'var(--muted-color)' }}
                      >
                        <h5 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-color)' }}>
                          Question {questionNumber}
                        </h5>

                        {question.question_photo ? (
                          question.question_photo === "InClass" ? (
                            <div
                              className="p-3 mb-3 rounded"
                              style={{
                                backgroundColor: 'var(--alt-bg)',
                                color: 'var(--text-color)'
                              }}
                            >
                              This question was presented in the class.
                            </div>
                          ) : (
                            <div className="mb-3">
                              <img
                                src={question.question_photo}
                                alt={`Question ${questionNumber}`}
                                className="mb-3 w-full rounded"
                              />
                              <button
                                onClick={() => handleImageRemove(session.id, questionNumber)}
                                className="px-3 py-1 mr-2 text-white rounded transition-colors"
                                style={{ backgroundColor: 'var(--primary-color)' }}
                                onMouseOver={(e) =>
                                  (e.currentTarget.style.backgroundColor = 'var(--hover-color)')
                                }
                                onMouseOut={(e) =>
                                  (e.currentTarget.style.backgroundColor = 'var(--primary-color)')
                                }
                              >
                                Remove Image
                              </button>
                            </div>
                          )
                        ) : null}

                        {(!question.question_photo || question.question_photo === "InClass") && (
                          <div className="mb-3">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                const key = `${session.id}-${questionNumber}`;
                                setSelectedFiles({
                                  ...selectedFiles,
                                  [key]: file,
                                });
                              }}
                              className="w-full p-2 border rounded focus:outline-none focus:ring mb-2"
                              style={{
                                backgroundColor: 'var(--card-bg)',
                                color: 'var(--text-color)',
                                borderColor: 'var(--muted-color)'
                              }}
                            />
                            <button
                              onClick={() => {
                                const key = `${session.id}-${questionNumber}`;
                                const file = selectedFiles[key];
                                if (file) {
                                  handleImageUpload(session.id, questionNumber, file);
                                } else {
                                  alert('Please select a file first.');
                                }
                              }}
                              className="px-3 py-1 text-white rounded transition-colors"
                              style={{ backgroundColor: 'var(--primary-color)' }}
                              onMouseOver={(e) =>
                                (e.currentTarget.style.backgroundColor = 'var(--hover-color)')
                              }
                              onMouseOut={(e) =>
                                (e.currentTarget.style.backgroundColor = 'var(--primary-color)')
                              }
                            >
                              Upload Image
                            </button>
                          </div>
                        )}

                        <p className="mb-2" style={{ color: 'var(--text-color)' }}>
                          <strong>Type:</strong> {questionTypeDisplay[question.question_type] || question.question_type}
                        </p>

                        {question.possible_answers && (
                          <div className="mb-2">
                            <strong style={{ color: 'var(--text-color)' }}>Possible Answers:</strong>
                            <ul className="list-disc ml-5 mt-2">
                              {question.possible_answers.map((answer, index) => (
                                <li key={index} style={{ color: 'var(--text-color)' }}>
                                  {answer}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <p className="mb-3" style={{ color: 'var(--text-color)' }}>
                          <strong>Correct Answer:</strong> {formatAnswer(question.correct_answer).join(', ') || 'Not set'}
                        </p>

                        <button
                          onClick={() => {
                            const existingAnswer = Array.isArray(question.correct_answer)
                              ? question.correct_answer.join(', ')
                              : question.correct_answer;
                            const newAnswer = prompt(
                              'Enter new correct answer:',
                              existingAnswer || ''
                            );
                            if (newAnswer !== null) {
                              updateCorrectAnswer(session.id, questionNumber, newAnswer);
                            }
                          }}
                          className="px-3 py-1 rounded transition-colors"
                          style={{
                            backgroundColor: 'var(--muted-color)',
                            color: 'var(--text-color)'
                          }}
                          onMouseOver={(e) =>
                            (e.currentTarget.style.backgroundColor = 'var(--card-hover-bg)')
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.backgroundColor = 'var(--muted-color)')
                          }
                        >
                          Modify Correct Answer
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

};

export default SessionList;
